'use client';

import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { contentfulClient } from '../';
import type { ReleaseNote, ReleaseNoteSkeleton } from '../types';
import { transformReleaseNoteEntry } from '../utils';

/**
 * Fetches a single release note by ID from Contentful
 *
 * @param id - The Contentful entry ID
 * @returns Single release note or null if not found
 */
async function fetchReleaseNote(id: string): Promise<ReleaseNote | null> {
  // Return null if Contentful is not configured
  if (!contentfulClient) {
    console.warn('Contentful client is not configured.');
    return null;
  }

  try {
    const entry = await contentfulClient.getEntry<ReleaseNoteSkeleton>(id);
    return transformReleaseNoteEntry(entry);
  } catch (error) {
    console.error('Error fetching release note:', error);
    return null;
  }
}

/**
 * Hook to fetch a single release note by ID from Contentful
 *
 * @param id - The Contentful entry ID
 * @param reactQueryOptions - Additional React Query options
 * @returns UseQueryResult containing a single release note
 *
 * @example
 * ```tsx
 * const { data: releaseNote, isLoading } = useGetReleaseNote('entry-id-123');
 * ```
 */
export function useGetReleaseNote(
  id: string | undefined,
  reactQueryOptions?: Omit<
    UseQueryOptions<ReleaseNote | null, Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<ReleaseNote | null, Error>({
    queryKey: ['contentful', 'release-note', id],
    queryFn: () => fetchReleaseNote(id!),
    enabled: Boolean(id),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    ...reactQueryOptions,
  });
}
